import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { theme } from "antd";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import type { ReportSummary } from "../../api";

/**
 * The chart above a grouped report.
 *
 * Design decisions worth not undoing:
 *
 * * **One series, always.** A report may measure several things (a count and an
 *   average), but plotting two measures of different scale on one plot means two
 *   y-scales, whose alignment is arbitrary and invents a correlation that is not
 *   in the data. So the measure is *switched*, never stacked, and the summary
 *   table beside it carries every measure at once.
 * * **One colour for every bar.** Shading bars by their own value double-encodes
 *   length as hue and spends the only free channel on information the bar length
 *   already shows. `#2a78d6` is a validated categorical slot-1 blue, checked
 *   against this app's white card surface.
 * * **Horizontal bars for categories, an area for time.** Category labels here
 *   are facility names and status names — long, and long labels fit down the
 *   side, not under a column. A period is the one dimension with a natural
 *   left-to-right order, so it gets a line.
 * * **Chrome recedes.** Hairline solid gridlines one step off the surface, no
 *   axis lines, no shadows, no legend for one series (the panel heading names
 *   it). The data is the only thing allowed to be loud.
 */

/** Validated categorical slot 1 — passes lightness, chroma and 3:1 on white. */
const SERIES = "#2a78d6";

const BAR_THICKNESS = 18; // ≤ 24px: the band keeps its air
const ROW_HEIGHT = 34;
const AXIS_BAND = 56; // room for the x-axis labels, so nothing clips

type Props = {
  summary: ReportSummary;
  metricId: string;
};

const SummaryChart = ({ summary, metricId }: Props) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  const metric =
    summary.metrics.find((m) => m.id === metricId) ?? summary.metrics[0];

  const options = useMemo<Highcharts.Options>(() => {
    const buckets = summary.buckets;
    const isPeriod = summary.group_by.is_period;
    const categories = buckets.map((bucket) => bucket.label);
    const data = buckets.map((bucket) => {
      const value = bucket.metrics[metric?.id ?? "count"];
      return value === null || value === undefined ? null : value;
    });

    const axisText = {
      color: token.colorTextTertiary,
      fontSize: "11px",
      fontVariantNumeric: "tabular-nums",
    };

    return {
      chart: {
        type: isPeriod ? "areaspline" : "bar",
        backgroundColor: "transparent",
        spacing: [8, 8, 0, 0],
        height: isPeriod
          ? 280
          : Math.max(160, buckets.length * ROW_HEIGHT + AXIS_BAND),
        style: {
          fontFamily:
            'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        },
      },
      title: { text: undefined },
      credits: { enabled: false },
      // One series: the panel heading already says what is plotted, so a
      // one-swatch legend box would only restate it.
      legend: { enabled: false },
      xAxis: {
        categories,
        lineWidth: 0,
        tickLength: 0,
        gridLineWidth: 0,
        labels: { style: axisText },
      },
      yAxis: {
        title: { text: undefined },
        gridLineWidth: 1,
        gridLineColor: token.colorSplit,
        gridLineDashStyle: "Solid",
        lineWidth: 0,
        // Whole counts must not tick in halves.
        allowDecimals: metric?.fn === "count" ? false : true,
        labels: { style: axisText },
      },
      plotOptions: {
        series: {
          animation: { duration: 240 },
          states: { hover: { brightness: 0.06 }, inactive: { opacity: 1 } },
        },
        bar: {
          color: SERIES,
          pointWidth: BAR_THICKNESS,
          // Rounds the data end only; the baseline stays square so every bar
          // starts from the same hard line.
          borderRadius: { radius: 4, scope: "point", where: "end" } as any,
          borderWidth: 0,
          groupPadding: 0.12,
          dataLabels: {
            enabled: true,
            // Outside the bar end: an interior label on a short bar either
            // clips or overflows, and both are worse than no label.
            inside: false,
            crop: false,
            overflow: "allow",
            // Text never wears the data colour — the bar beside it carries
            // identity, the label carries the value.
            style: {
              color: token.colorTextSecondary,
              fontSize: "12px",
              fontWeight: "600",
              textOutline: "none",
              fontVariantNumeric: "tabular-nums",
            },
          },
        },
        areaspline: {
          color: SERIES,
          lineWidth: 2,
          fillOpacity: 0.1,
          marker: {
            enabled: true,
            radius: 4,
            fillColor: SERIES,
            // A 2px surface ring keeps a marker legible where it crosses the
            // line, and enlarges the hover target.
            lineWidth: 2,
            lineColor: token.colorBgContainer,
            states: { hover: { radiusPlus: 2 } },
          },
        },
      },
      tooltip: {
        backgroundColor: token.colorBgElevated,
        borderColor: token.colorBorderSecondary,
        borderRadius: 8,
        borderWidth: 1,
        shadow: false,
        padding: 10,
        style: { color: token.colorText, fontSize: "12px" },
        headerFormat: '<span style="font-size:11px;opacity:.65">{point.key}</span><br/>',
        pointFormatter(this: Highcharts.Point) {
          const label = metric?.title ?? "";
          const value = this.y === null ? "—" : Highcharts.numberFormat(this.y!, -1);
          return `<b style="font-size:14px">${value}</b> <span style="opacity:.65">${label}</span>`;
        },
      },
      series: [
        {
          type: isPeriod ? "areaspline" : "bar",
          name: metric?.title ?? t("Requests"),
          data,
        } as Highcharts.SeriesOptionsType,
      ],
    };
  }, [summary, metric, token, t]);

  if (!summary.buckets.length) return null;

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={options}
      // Re-render on metric switch rather than mutating the live chart, so a
      // stale axis never survives the change.
      key={`${metric?.id}-${summary.group_by.key}`}
    />
  );
};

export default SummaryChart;
